// PostDetail.js – Detailansicht für einzelne Posts mit SEO/JSON-LD
import React from "react";
import { useParams, Link } from "react-router-dom";
import { usePost } from "../api/posts";
import SEO from "../components/SEO";
import { getImageUrl } from "../utils/imageUtils";
import { buildProfilePath } from "../utils/profileUrl";
import { CalendarDays, User, ExternalLink } from "lucide-react";

function formatDateTime(dt) {
    if (!dt) return "";
    const d = new Date(dt);
    return d.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
}

const PostDetail = () => {
    const { slug } = useParams();
    const { data: post, isLoading, error } = usePost(slug);

    if (isLoading) {
        return <div className="py-16 text-center text-secondary-600">Загрузка поста…</div>;
    }
    if (error || !post) {
        return <div className="py-16 text-center text-red-600">Пост не найден.</div>;
    }

    const ownerProfileUrl = buildProfilePath({
        profile: {
            username_slug: post.owner?.username_slug,
            username: post.owner?.username,
            id: post.owner_user_id,
        },
        categoryPath: {
            main: { slug: post.owner?.category?.main_slug },
            sub: { slug: post.owner?.category?.sub_slug },
            subSub: { slug: post.owner?.category?.sub_sub_slug },
        },
    });

    // JSON-LD für SEO (SocialMediaPosting)
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "SocialMediaPosting",
        headline: post.title || post.body?.slice(0, 120) || "",
        articleBody: post.body,
        image: post.image_url ? getImageUrl(post.image_url) : undefined,
        datePublished: post.published_at,
        author: {
            "@type": "Person",
            name: post.owner?.title || post.owner?.username,
            url: ownerProfileUrl,
        },
        url: `/posts/${post.slug}`,
        mainEntityOfPage: `/posts/${post.slug}`,
    };

    return (
        <>
            <SEO
                title={post.title || post.body?.slice(0, 60) || "Пост"}
                description={post.body?.slice(0, 160) || post.title}
                url={`/posts/${post.slug}`}
                type="article"
                canonical={`/posts/${post.slug}`}
                structuredData={structuredData}
            />
            <div className="max-w-3xl mx-auto px-4 py-8">
                <Link to="/events?type=posts" className="text-primary-700 hover:underline text-sm mb-4 inline-block">
                    &larr; К списку постов
                </Link>
                <div className="table w-full bg-white/80 dark:bg-secondary-900/80 rounded-xl shadow-soft p-6">
                   
                        {post.image_url && (
                            
                                <img
                                    src={getImageUrl(post.image_url)}
                                    alt="Post"
                                    className="post-img object-cover rounded-lg"
                                    loading="lazy"
                                />
                           
                        )}

                        <div className="flex items-center gap-3 text-secondary-700 text-sm mb-4">
                            <CalendarDays className="w-4 h-4 flex-shrink-0" />
                            <span className="break-words">
                                {formatDateTime(post.published_at)}
                            </span>
                        </div>
                        <div className="font-bold text-xl text-secondary-900 break-words mb-4">{post.title}</div>
                        <div className="text-secondary-900 break-words whitespace-pre-wrap mb-4">{post.body}</div>
                        {post.link_url && (
                            <a
                                href={post.link_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-primary-700 hover:underline text-sm mt-2"
                            >
                                <ExternalLink className="w-4 h-4" />
                                {post.link_url}
                            </a>
                        )}
                        <div className="flex items-center gap-3 text-secondary-700 text-sm">
                            <User className="w-4 h-4 flex-shrink-0" />
                            <Link to={ownerProfileUrl} className="hover:underline">
                                {post.owner?.title || post.owner?.username}
                            </Link>
                        </div>
                    
                </div>
            </div>
        </>
    );
};

export default PostDetail;
